import { useRef, useState, useEffect } from "react";
import { Navigation, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react/swiper-react";
import AddProduct from "../Global/AddProduct/AddProduct";
import CtaLink from "../Global/CtaLink";
import LinkWrapper from "../Global/LinkWrapper";
import {
  DesktopNav as Desktop,
  MobileNav as Mobile,
  IsDesktopNav,
} from "../../media-queries/MediaQueries";
import { isAuthored, getUrlPath } from "../../helpers/LinkHelper";
import { SIZE } from "../Global/AddProduct/constants";
import { COMPATIBLEPRODUCTSTABID, HEADING_LEVELS } from "../Global/GlobalConstants";
import ErrorComponent from "../Common/ErrorComponent/ErrorComponent";
import { Link } from "react-router-dom";
import "./CompatibleProducts.scss";
import { useProductCache } from "../../hooks/useProductCache";
import Image from "../Global/Image/Image";

import "swiper/swiper.scss";
import "swiper/modules/a11y/a11y.scss";
import "swiper/modules/navigation/navigation.scss";
import Title from "../Global/Title/Title";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import LoadingContainer from "../Common/LoadingContainer/LoadingContainer";
import useLoadingState from "../../hooks/useLoadingState";

const MAXPRODUCTS = 6;
const MOBILEPRODUCTSLIDES = 2;
const TABLETPRODUCTSLIDES = 3;
const SMALLDESKTOPPRODUCTSLIDES = 4;
const DESKTOPPRODUCTSLIDES = 6;

const PRODUCTGRIDMODE = false; // Made a flag in case marketing ever wants to go back to grid mode, or add a UI toggle

function ProductTile({ product, id }) {
  const { catalogImage, childProductName, productCode, url } = product;
  const sendDataToGTM = useGTMDispatch();
  const isDesktop = IsDesktopNav();
  const viewDetailsForProduct = ((window.globalProperties && window.globalProperties['viewDetailsForProduct']) || 'View Details for') + ' ' +  productCode;

  const proPlanetSustainableLabel = window.globalProperties?.["proPlanetSustainableLabel"]
    ? window.globalProperties?.["proPlanetSustainableLabel"]
    : "ProPlanet";

  const handleProductClick = () => {
    sendDataToGTM({
      event: "carouselClick",
      productName: childProductName,
      listType: "Compatible",
    });
  };

   return (
    (catalogImage || childProductName) && (
      <div className="compatible-products__carousel__card">
        <div className="product-collection__flag-section">
          {product?.isProPlanet && (
            <p className="pdp-header__active-image-pdp-icon" aria-label={proPlanetSustainableLabel}>
              <img src="https://dart.widen.net/content/vsjmstuwtr/svg/ProPlanet-Leaf-CheckmarkwStroke-Green.svg?u=hsm95h" alt={proPlanetSustainableLabel}/>
            </p>
          )}
        </div>
        <Link
          className="compatible-products__carousel__card__link"
          onClick={handleProductClick}
          to={getUrlPath(url)}
          id={id}
        >
          <div className="compatible-products__carousel__card__image-wrapper">
              <Image
                className="compatible-products__carousel__card__image"
                src={catalogImage}
                loading="lazy"
                alt={viewDetailsForProduct}
              />
              {childProductName && (
                <p className="compatible-products__carousel__card__name bold-extra-bold">
                  {childProductName}
                </p>
              )}
          </div>

        </Link>
        <AddProduct
          productCode={productCode}
          productName={childProductName}
          btnSize={isDesktop ? SIZE.LARGE : SIZE.MEDIUM}
          addToListAnalytics={{
            event: "listAdd",
            listType: "Compatible",
            productName: `${childProductName}`,
          }}
          removeFromListAnalytics={{
            event: "removeFromList",
            listType: "Compatible",
            productName: `${childProductName}`,
          }}
        />
      </div>
    )
  );
}

function SwiperCarousel({ limit = MAXPRODUCTS, productsList }) {
  const carouselContainerRef = useRef(null);
  return (
    <div
      className={`compatible-products__carousel ${productsList.length > 1 ? "" : "hide-arrows"}`}
      ref={carouselContainerRef}
    >
      <Swiper
        key={productsList.length}
        navigation
        modules={[Navigation, A11y]}
        loop={false}
        a11y={{
          prevSlideMessage: (window.globalProperties && window.globalProperties["prevSlideMessage"]) || "Previous slide",
          nextSlideMessage: (window.globalProperties && window.globalProperties["nextSlideMessage"]) || "Next slide"
        }}
        breakpoints={{
          0: {
            slidesPerView: MOBILEPRODUCTSLIDES,
            spaceBetween: 10,
            touchRatio: 1,
          },
          640: {
            slidesPerView: TABLETPRODUCTSLIDES,
            spaceBetween: 10,
            touchRatio: 1,
          },
          992: {
            slidesPerView: SMALLDESKTOPPRODUCTSLIDES,
            spaceBetween: 10,
            touchRatio: 1,
            a11y: false,
          },
          1150: {
            slidesPerView: DESKTOPPRODUCTSLIDES,
            spaceBetween: 10,
            touchRatio: 0,
            a11y: false,
          },
          1440: {
            slidesPerView: DESKTOPPRODUCTSLIDES,
            spaceBetween: 20,
            touchRatio: 0,
            a11y: false,
          }
        }}
      >
        {productsList.map((product, index) => {
          return (
            <SwiperSlide key={product.productCode}>
              <ProductTile
                product={product}
                addToListAnalytics={{
                  event: "listAdd",
                  listType: "Compatible",
                  productName: `${product.childProductName}`,
                }}
                removeFromListAnalytics={{
                  event: "removeFromList",
                  listType: "Compatible",
                  productName: `${product.childProductName}`,
                }}
                id={index === 0 ? `${COMPATIBLEPRODUCTSTABID}_focus` : null}
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}

function ProductGrid({ limit, viewMoreLink, productsList, productCode }) {
  const productsListLength = productsList.length;
  if (productsList.length > limit) {
    productsList = productsList.slice(0, limit);
  }

  const showViewMoreLink = isAuthored(viewMoreLink);

  return (
    <div className="compatible-products__product-grid">
      <div className="compatible-products__product-grid__products">
        {productsList.map((product, index) => {
          return (
            <ProductTile
              key={product.productCode}
              product={product}
              addToListAnalytics={{
                event: "listAdd",
                listType: "Compatible",
                productName: `${product.childProductName}`,
              }}
              removeFromListAnalytics={{
                event: "removeFromList",
                listType: "Compatible",
                productName: `${product.childProductName}`,
              }}
              id={index === 0 ? `${COMPATIBLEPRODUCTSTABID}_focus` : null}
            />
          );
        })}
        {showViewMoreLink && productsListLength - limit > 0 && (
          <LinkWrapper
            icon="icon darticon-largearrow"
            link={viewMoreLink}
            queryString={`?filters=compatibleProducts:${productCode}`}
            className="compatible-products__carousel__card compatible-products__product-grid__products__view-more"
          />
        )}
      </div>
    </div>
  );
}

function ProductDetailCompatibleProducts({ component, page }) {
  const { document: documentRef } = component.getModels();
  const document = page.getContent(documentRef);
  const { product } = page.getComponent().getModels();
  const { productServletURL } = page.getComponent("header").getModels();
  const [compatibleProducts, setCompatibleProducts] = useState(null);
  const { isLoading, setSuccess } = useLoadingState(true);

  // Fetch compatible products with caching
  const compatibleProductCodes = product?.productData?.compatibleProducts || [];
  const { products: productsData = [] } = useProductCache(
    compatibleProductCodes,
    productServletURL,
    null,
    null,
    !!(compatibleProductCodes.length && productServletURL)
  );

  useEffect(() => {
    if (productsData && productsData.length > 0) {
      setCompatibleProducts(productsData);
      setSuccess();
    } else if (compatibleProductCodes.length === 0) {
      setSuccess();
    }
  }, [productsData, compatibleProductCodes.length, setSuccess]);

  try {
    if (!product) return null;
    if (!document) return null;
    if (!product?.productData?.compatibleProducts) return null;

    const {
      title = null,
      maxProductsToShow = MAXPRODUCTS,
      viewAllLink = null,
      viewMoreLink = {},
    } = document.getData();
    const showViewAllLink = isAuthored(viewAllLink);

    let copiedViewMoreLink = Object.assign({}, viewMoreLink);
    if (
      copiedViewMoreLink &&
      copiedViewMoreLink.label &&
      compatibleProducts?.length - maxProductsToShow > 0
    ) {
      copiedViewMoreLink.label = copiedViewMoreLink.label.replace(
        "{}",
        compatibleProducts.length - maxProductsToShow
      );
    }

    return (
      <div className="compatible-products" id={COMPATIBLEPRODUCTSTABID}>
        <div className={`compatible-products__inner${PRODUCTGRIDMODE ? "grid" : ""}`}>
          {title && (
            <div className="compatible-products__title-wrapper">
              <Title defaultLevel={HEADING_LEVELS.TWO} className="compatible-products__title">
                {title}
              </Title>
            </div>
          )}
          <LoadingContainer
            isLoading={isLoading}
            isEmpty={!compatibleProducts || compatibleProducts.length === 0}
            emptyMessage="No compatible products available"
            loadingMessage="Loading compatible products"
            showAlert={true}
          >
            {PRODUCTGRIDMODE ? (
            <>
              <Mobile>
                <SwiperCarousel limit={maxProductsToShow} productsList={[...compatibleProducts]} />
              </Mobile>

              <Desktop>
                <ProductGrid
                  limit={maxProductsToShow}
                  viewMoreLink={copiedViewMoreLink}
                  productsList={compatibleProducts}
                  productCode={product.productCode}
                />
              </Desktop>
            </>
          ) : (
            <SwiperCarousel limit={maxProductsToShow} productsList={compatibleProducts} />
          )}
          </LoadingContainer>
        </div>
        {showViewAllLink && (
          <Mobile>
            <div className="compatible-products__cta-wrapper">
              <CtaLink
                className="btn btn--secondary"
                link={viewAllLink}
                queryString={`?filters=compatibleProducts:${product.productCode}`}
              />
            </div>
          </Mobile>
        )}
      </div>
    );
  } catch (e) {
    console.error("ProductDetailCompatibleProducts error", e);
    return <ErrorComponent error={e} componentName={"ProductDetailCompatibleProducts"} />;
  }
}

export default ProductDetailCompatibleProducts;
